import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const COLORS = ['#18181b', '#52525b', '#a1a1aa', '#d4d4d8', '#f4f4f5'];
const PIE_COLORS = ['#18181b', '#71717a', '#a1a1aa', '#d4d4d8'];

const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: -15 };
const CHART_MARGIN_VERTICAL = { top: 5, right: 20, bottom: 5, left: 5 };
const CHART_MARGIN_REVENUE = { top: 5, right: 30, bottom: 5, left: 10 };
const AXIS_TICK_STYLE = { fontSize: 11, fill: '#a1a1aa' };
const AXIS_TICK_SMALL = { fontSize: 10, fill: '#52525b' };
const TOOLTIP_STYLE = { fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 2 };
const GRID_STROKE = "#f4f4f5";

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border border-zinc-200 p-4 rounded-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
  </div>
);

const MiniPie = ({ data, title }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  return (
    <div className="bg-white border border-zinc-200 p-4 rounded-sm">
      <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">{title}</p>
      <div className="flex items-center gap-4">
        <div className="w-24 h-24">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={22} outerRadius={40} dataKey="value" stroke="none">
                {data.map((entry, i) => <Cell key={entry.name || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5 flex-1">
          {data.map((d, i) => (
            <div key={d.name || i} className="flex items-center gap-2 text-sm">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
              <span className="text-zinc-600 flex-1">{d.name}</span>
              <span className="font-semibold tabular-nums">{d.value}</span>
              <span className="text-xs text-zinc-400">({total > 0 ? Math.round(d.value / total * 100) : 0}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function TechStats({ stats }) {
  if (!stats || !stats.monthly_data) return null;
  return (
    <div className="space-y-4" data-testid="tech-stats-section">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Aujourd'hui" value={stats.orders_today} />
        <StatCard label="En attente" value={stats.pending} />
        <StatCard label="En cours" value={stats.in_progress} />
        <StatCard label="Terminées" value={stats.completed} />
        <StatCard label="Refabrications" value={stats.refabrications} />
        <StatCard label="Délai moyen" value={stats.avg_fab_hours > 0 ? `${stats.avg_fab_hours}h` : '-'} sub="validation → livraison" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Monthly orders bar chart */}
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">Commandes par mois</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly_data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="label" tick={AXIS_TICK_STYLE} />
                <YAxis tick={AXIS_TICK_STYLE} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Commandes" fill="#18181b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top products */}
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">Top 5 produits</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_products} layout="vertical" margin={CHART_MARGIN_VERTICAL}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK_STYLE} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={AXIS_TICK_SMALL} width={140} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Commandes" fill="#52525b" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Pie charts row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniPie data={stats.type_breakdown} title="Type de fabrication" />
        <MiniPie data={stats.material_breakdown} title="Matière" />
        <MiniPie data={stats.side_breakdown} title="Côté" />
        <MiniPie data={stats.reception_breakdown} title="Réception empreintes" />
      </div>
    </div>
  );
}

export function AdminStats({ stats }) {
  if (!stats || !stats.revenue_data) return null;
  return (
    <div className="space-y-4" data-testid="admin-stats-section">
      {/* Admin KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Aujourd'hui" value={stats.orders_today} />
        <StatCard label="Total commandes" value={stats.total} />
        <StatCard label="CA total" value={`${stats.total_revenue?.toLocaleString('fr-FR')} €`} />
        <StatCard label="Refabrications" value={`${stats.refab_rate}%`} sub={`${stats.refabrications} commandes`} />
        <StatCard label="Délai moyen" value={stats.avg_fab_hours > 0 ? `${stats.avg_fab_hours}h` : '-'} sub="validation → livraison" />
        <StatCard label="Clients actifs" value={stats.top_clients?.length || 0} />
      </div>

      {/* Revenue + orders charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">CA mensuel (€)</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.revenue_data} margin={CHART_MARGIN_REVENUE}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="label" tick={AXIS_TICK_STYLE} />
                <YAxis tick={AXIS_TICK_STYLE} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`${v.toLocaleString('fr-FR')} €`, 'CA']} />
                <Line type="monotone" dataKey="revenue" stroke="#18181b" strokeWidth={2} dot={{ r: 3, fill: '#18181b' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">Commandes par mois</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.monthly_data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="label" tick={AXIS_TICK_STYLE} />
                <YAxis tick={AXIS_TICK_STYLE} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Commandes" fill="#18181b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top products + pie charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium mb-3">Top 5 produits</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.top_products} layout="vertical" margin={CHART_MARGIN_VERTICAL}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK_STYLE} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={AXIS_TICK_SMALL} width={140} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Commandes" fill="#52525b" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <MiniPie data={stats.type_breakdown} title="Type" />
          <MiniPie data={stats.material_breakdown} title="Matière" />
          <MiniPie data={stats.side_breakdown} title="Côté" />
          <MiniPie data={stats.reception_breakdown} title="Réception" />
        </div>
      </div>

      {/* Top clients table */}
      {stats.top_clients && stats.top_clients.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-4 py-3 border-b border-zinc-200">
            <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Top clients</p>
          </div>
          <table className="w-full">
            <thead><tr className="border-b border-zinc-100">
              <th className="text-left px-4 py-2 text-xs text-zinc-400 uppercase tracking-wider font-medium">Client</th>
              <th className="text-left px-4 py-2 text-xs text-zinc-400 uppercase tracking-wider font-medium">Entreprise</th>
              <th className="text-right px-4 py-2 text-xs text-zinc-400 uppercase tracking-wider font-medium">Commandes</th>
              <th className="text-right px-4 py-2 text-xs text-zinc-400 uppercase tracking-wider font-medium">CA</th>
            </tr></thead>
            <tbody>
              {stats.top_clients.map((c, i) => (
                <tr key={c.name || i} className="border-b border-zinc-50 hover:bg-zinc-50">
                  <td className="px-4 py-2.5 text-sm font-medium">{c.name}</td>
                  <td className="px-4 py-2.5 text-sm text-zinc-500">{c.company}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-semibold tabular-nums">{c.count}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-mono">{c.revenue.toLocaleString('fr-FR')} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
